/**
 * @module Browser
 * Browser entry point for the Vana SDK.
 *
 * @remarks
 * Exposes the platform adapter, ECIES crypto, storage providers, smart-contract
 * helpers, ABIs, addresses, chain configurations, and shared types — without
 * Node-only dependencies.
 */

// Type re-exports
export type * from "./types";

// Errors
export * from "./errors";

// Contract controller (L1 + smart contract integration)
export * from "./contracts/contractController";

// ABIs
export { getAbi } from "./generated/abi";
export type {
  VanaContract,
  VanaContract as VanaContractAbi,
} from "./generated/abi";

// Addresses
export { getContractAddress, CONTRACTS } from "./generated/addresses";

// Chain configurations
export {
  vanaMainnet,
  mokshaTestnet,
  moksha,
  type VanaChainConfig,
  getChainConfig,
  getAllChains,
} from "./chains";
export * from "./chains";

// viem chain registry
export { chains } from "./config/chains";

// Default service endpoints
export {
  type ServiceEndpoints,
  mainnetServices,
  mokshaServices,
  getServiceEndpoints,
} from "./config/default-services";

// Storage API
export * from "./storage/index";
export { createVanaStorageProvider } from "./storage/default";
export type { VanaStorageProviderOptions } from "./storage/default";

// Platform adapters - browser-safe exports
export { BrowserPlatformAdapter } from "./platform/browser";
export { BrowserECIESUint8Provider as BrowserECIESProvider } from "./crypto/ecies/browser";
export type { VanaPlatformAdapter } from "./platform/interface";

// ECIES utilities
export {
  ECIESError,
  isECIESEncrypted,
  serializeECIES,
  deserializeECIES,
} from "./crypto/ecies";
export type {
  ECIESProvider,
  ECIESEncrypted,
  ECIESOptions,
} from "./crypto/ecies";

// Browser-only platform adapter utilities
export {
  createBrowserPlatformAdapter,
  createPlatformAdapterSafe,
} from "./platform/browser-only";

// Auth primitives
export {
  generatePkceVerifier,
  computePkceChallenge,
  verifyPkceChallenge,
  assertValidPkceVerifier,
  PKCE_VERIFIER_PATTERN,
  PKCE_CHALLENGE_PATTERN,
} from "./auth/pkce";
export {
  InMemoryTokenStore,
  type TokenStore,
  type TokenRecord,
} from "./auth/token-store";
export {
  OAuthClient,
  type OAuthClientConfig,
  type AuthorizationUrlResult,
} from "./auth/oauth-client";

// Note: createNodePlatformAdapter is intentionally not exported in browser bundle
// to avoid bundling Node-only dependencies.

// Platform utilities - browser-safe only
export {
  detectPlatform,
  isPlatformSupported,
  getPlatformCapabilities,
} from "./platform/utils";

// HKDF / master key derivation (DPv1 envelope)
export {
  deriveMasterKey,
  deriveScopeKey,
  recoverServerOwner,
  MASTER_KEY_MESSAGE,
} from "./crypto/keys/derive";

// OpenPGP file encryption (DPv1 envelope)
export {
  encryptWithPassword,
  decryptWithPassword,
} from "./crypto/envelope/openpgp";

// Web3Signed auth primitives
export {
  parseWeb3SignedHeader,
  verifyWeb3Signed,
  type Web3SignedPayload,
  type VerifiedAuth,
} from "./auth/web3-signed";
export {
  buildWeb3SignedHeader,
  computeBodyHash,
  type Web3SignedSignFn,
} from "./auth/web3-signed-builder";
export {
  MissingAuthError,
  InvalidSignatureError,
  ExpiredTokenError,
} from "./auth/errors";

// Data Portability protocol signing helpers
export {
  fileRegistrationDomain,
  grantRegistrationDomain,
  grantRevocationDomain,
  serverRegistrationDomain,
  builderRegistrationDomain,
  FILE_REGISTRATION_TYPES,
  GRANT_REGISTRATION_TYPES,
  GRANT_REVOCATION_TYPES,
  SERVER_REGISTRATION_TYPES,
  BUILDER_REGISTRATION_TYPES,
  type DataPortabilityContracts,
  type DataPortabilityGatewayConfig,
  type FileRegistrationMessage,
  type GrantRegistrationMessage,
  type GrantRevocationMessage,
  type ServerRegistrationMessage,
  type BuilderRegistrationMessage,
} from "./protocol/eip712";
export {
  PERSONAL_SERVER_REGISTRATION_DEFAULT_CHAIN_ID,
  PERSONAL_SERVER_REGISTRATION_DEFAULT_VERIFYING_CONTRACT,
  personalServerRegistrationDomain,
  createViemPersonalServerRegistrationSigner,
  buildPersonalServerRegistrationTypedData,
  buildPersonalServerRegistrationSignature,
  registerPersonalServerSignature,
  type PersonalServerRegistrationTypedData,
  type PersonalServerRegistrationSigner,
  type PersonalServerRegistrationDomainInput,
  type ViemPersonalServerRegistrationWalletClient,
  type ViemPersonalServerRegistrationSignerSource,
  type BuildPersonalServerRegistrationTypedDataInput,
  type BuildPersonalServerRegistrationSignatureInput,
  type PersonalServerRegistrationSignature,
} from "./protocol/personal-server-registration";
export {
  PERSONAL_SERVER_LITE_OWNER_BINDING_VERSION,
  PERSONAL_SERVER_LITE_OWNER_BINDING_PURPOSE,
  PERSONAL_SERVER_LITE_OWNER_BINDING_PREFIX,
  buildPersonalServerLiteOwnerBindingMessage,
  createViemPersonalServerLiteOwnerBindingSigner,
  buildPersonalServerLiteOwnerBindingSignature,
  signPersonalServerLiteOwnerBinding,
  type PersonalServerLiteOwnerBindingPurpose,
  type PersonalServerLiteOwnerBindingMessage,
  type PersonalServerLiteOwnerBindingSigner,
  type ViemPersonalServerLiteOwnerBindingWalletClient,
  type ViemPersonalServerLiteOwnerBindingSignerSource,
  type BuildPersonalServerLiteOwnerBindingSignatureInput,
  type PersonalServerLiteOwnerBindingSignature,
} from "./protocol/personal-server-lite-owner-binding";
export {
  ACCOUNT_PERSONAL_SERVER_REGISTRATION_INTENT,
  AccountPersonalServerRegistrationError,
  signPersonalServerRegistrationWithAccount,
  type AccountPersonalServerRegistrationIntent,
  type AccountPersonalServerRegistrationSignature,
  type AccountPersonalServerRegistrationStatus,
  type AccountPersonalServerRegistrationRequest,
  type AccountPersonalServerRegistrationConfig,
  type AccountSignedPersonalServerRegistration,
  type AccountConfirmationRequiredPersonalServerRegistration,
  type AccountFallbackSignedPersonalServerRegistration,
  type AccountPersonalServerRegistrationResult,
} from "./account/personal-server-registration";
export {
  AccountPersonalServerLiteOwnerBindingError,
  signPersonalServerLiteOwnerBindingWithAccountClient,
  type AccountPersonalServerLiteOwnerBindingClient,
  type SignPersonalServerLiteOwnerBindingWithAccountClientConfig,
} from "./account/personal-server-lite-owner-binding";
export {
  isDataPortabilityGatewayConfig,
  parseGrantRegistrationPayload,
  verifyGrantRegistration,
  type DataPortabilityGrantPayload,
  type VerifyGrantRegistrationInput,
  type VerifyGrantRegistrationResult,
} from "./protocol/grants";
export {
  ScopeSchema,
  parseScope,
  scopeToPathSegments,
  scopeMatchesPattern,
  scopeCoveredByGrant,
  type Scope,
  type ParsedScope,
} from "./protocol/scopes";
export {
  DataFileEnvelopeSchema,
  createDataFileEnvelope,
  IngestResponseSchema,
  type DataFileEnvelope,
  type IngestResponse,
} from "./protocol/data-file";
export {
  createGatewayClient,
  type GatewayEnvelope,
  type GatewayProof,
  type Builder,
  type Schema,
  type ServerInfo,
  type GatewayGrantResponse,
  type GrantListItem,
  type FileRecord,
  type FileListResult,
  type RegisterServerParams,
  type RegisterServerResult,
  type RegisterFileParams,
  type CreateGrantParams,
  type RevokeGrantParams,
  type GatewayClient,
} from "./protocol/gateway";

// Personal Server typed errors
export { PSError, parsePSError, type PSErrorCode } from "./types/ps-errors";
